import router from '@/router';
import {defineStore} from 'pinia';
import {computed, ref, watch} from 'vue';
import {RouteLocationNormalized as Route} from 'vue-router';
import {settingsNames, Settings, useSettings} from './settings';

export interface ApplicationState {
  savedTitle: string | null;
  route: Route | null;
  wantedRoute: Route | null;
  neededSettings: Array<keyof Settings>,
  needsAuthentication: boolean | null;
}


export const useApplicationState = defineStore('applicationState', () => {
  const settingsStore = useSettings();
  const savedTitle = ref<string | null>(null);
  const route = ref<Route | null>(null);
  const wantedRoute = ref<Route | null>(null);
  const neededSettings = ref<Array<keyof Settings>>([]);
  const needsAuthentication = ref<boolean | null>(null);
  const updateTitle = (newTitle: string | null) => {
    savedTitle.value = newTitle;
  };
  const reset = () => {
    neededSettings.value = [];
    needsAuthentication.value = null;
    wantedRoute.value = null;
  };
  const updateRoute = (newRoute: Route) => {
    if (newRoute.fullPath === wantedRoute.value?.fullPath) {
      // Hat die gewollte Route erreicht, also zurücksetzen
      reset();
    }
    route.value = newRoute;
  };
  const wantsRoute = (route: Route | null = null) => {
    if (wantedRoute.value) return; // wantedRoute bereits gesetzt

    wantedRoute.value = route;
    neededSettings.value = route?.meta?.neededSettings as Array<keyof Settings> ?? [];
    needsAuthentication.value = !route?.meta?.allowUnauthenticated;
  };
  const validate = () => {
    if (wantedRoute.value) return; // wantedRoute bereits gesetzt
    wantsRoute(route.value);
  };

  const title = computed((): string => {
    return savedTitle.value ?? route.value?.meta?.title as string ?? route.value?.name as string ?? 'Service-ERP';
  });

  const blockedBySettings = computed(() => {
    for (const settingName of neededSettings.value) {
      const setting: Settings[keyof Settings] = settingsStore[settingName];
      if (!setting && setting !== 0) {
        return true;
      }
    }

    return false;
  });
  const blockedByLogin = computed(() => {
    if (needsAuthentication.value && !settingsStore.userToken) {
      return true;
    }

    return false;
  });
  const nextRoute = computed((): string | null => {
    if (blockedByLogin.value) {
      if (route.value?.name !== 'login') return '/login';
      else return null;
    }
    if (blockedBySettings.value) {
      if (route.value?.name !== 'settings') return '/settings';
      else return null;
    }
    if (!blockedByLogin.value && route.value?.name === 'login') return '/';

    return wantedRoute.value?.fullPath ?? null;
  });
  const missingSettings = computed((): string[] => {
    return neededSettings.value
        .filter((s) => !settingsStore[s] && settingsStore[s] !== 0)
        .map((s) => settingsNames[s]);
  });

  watch(nextRoute, async (value, oldValue) => {
    if (value !== oldValue && value) {
      await router.replace(value).catch(console.error);
    }
  });

  return {
    savedTitle,
    route,
    wantedRoute,
    neededSettings,
    needsAuthentication,
    updateTitle,
    updateRoute,
    wantsRoute,
    validate,
    reset,
    title,
    blockedBySettings,
    blockedByLogin,
    nextRoute,
    missingSettings,
  };
});
